import { Link, useLocation } from 'react-router-dom'
import {
  BarChart3,
  Bell,
  Building2,
  Calendar,
  FileText,
  Heart,
  Home,
  QrCode,
  User,
  CreditCard,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useNotificationRefresh } from '../../contexts/NotificationContext'

const MAIN_NAV = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Mascotas', href: '/mascotas', icon: Heart },
  { name: 'Citas', href: '/citas', icon: Calendar },
  { name: 'Fichas Clínicas', href: '/fichas', icon: FileText },
  { name: 'Acceso QR', href: '/acceso-mascotas', icon: QrCode },
]

const CLINIC_NAV = [
  { name: 'Mi Clínica', href: '/clinicas', icon: Building2 },
  { name: 'Veterinarios', href: '/veterinarios', icon: User },
  { name: 'Suscripciones Pendientes', href: '/suscripciones-pendientes', icon: CreditCard },
  { name: 'Reportes', href: '/reportes', icon: BarChart3 },
  { name: 'Notificaciones', href: '/notificaciones', icon: Bell, useUnreadCount: true },
]

function getInitials(user) {
  const source = `${user?.nombre || ''} ${user?.apellido || ''}`.trim()
  if (!source) return 'VO'
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
}

function getClinicName(user) {
  const vet = user?.veterinarios?.[0] || {}
  return (
    vet?.clinica?.nombre ||
    vet?.clinica?.name ||
    vet?.nombre_clinica ||
    vet?.nombreClinica ||
    user?.clinica?.nombre ||
    'Mi Clínica'
  )
}

function isActivePath(pathname, href) {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

export default function VetOwnerSidebar({ onNavigate }) {
  const location = useLocation()
  const { user } = useAuth()
  const { contadorNoLeidas } = useNotificationRefresh()
  const unreadCount = Number(contadorNoLeidas) > 0 ? Number(contadorNoLeidas) : 0
  const clinicName = getClinicName(user)

  return (
    <aside className="w-[230px] bg-[#151e2d] border-r border-[#243246] flex flex-col h-full">
      <div className="px-4 py-5 border-b border-[#243246] flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-[10px] bg-[#00c896] text-[#0f1623] flex items-center justify-center text-base">
          🐾
        </div>
        <div>
          <p className="text-[15px] font-semibold text-[#e8edf5] leading-none">Artemis</p>
          <p className="text-[10px] text-[#7a8fa6] mt-0.5 leading-none">Sistema de Gestión</p>
        </div>
      </div>

      <div className="mx-3 mt-3 mb-2 bg-[rgba(255,183,77,0.12)] border border-[rgba(255,183,77,0.3)] rounded-lg px-3 py-2">
        <div className="flex items-start gap-2">
          <span className="text-[14px]">🏥</span>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-[#ffb74d]">Veterinario Dueño</p>
            <p className="text-[10px] text-[#7a8fa6] truncate">{clinicName}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-2.5 py-1 overflow-y-auto">
        {MAIN_NAV.map((item) => {
          const active = isActivePath(location.pathname, item.href)
          const Icon = item.icon
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={[
                'flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13px] transition-colors',
                active
                  ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
                  : 'text-[#7a8fa6] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#e8edf5]',
              ].join(' ')}
            >
              <Icon className="w-[17px] h-[17px] shrink-0" />
              <span className="truncate">{item.name}</span>
            </Link>
          )
        })}

        <div className="text-[10px] uppercase tracking-[0.08em] text-[#3d5166] font-medium px-3 pt-4 pb-1">
          Mi clínica
        </div>

        {CLINIC_NAV.map((item) => {
          const active = isActivePath(location.pathname, item.href)
          const Icon = item.icon
          const badgeCount = item.useUnreadCount ? unreadCount : 0

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavigate}
              className={[
                'flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[13px] transition-colors',
                active
                  ? 'bg-[rgba(0,200,150,0.12)] text-[#00c896]'
                  : 'text-[#7a8fa6] hover:bg-[rgba(0,200,150,0.06)] hover:text-[#e8edf5]',
              ].join(' ')}
            >
              <Icon className="w-[17px] h-[17px] shrink-0" />
              <span className="truncate">{item.name}</span>
              {badgeCount > 0 ? (
                <span className="ml-auto bg-[#ef5350] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              ) : null}
            </Link>
          )
        })}
      </nav>

      <div className="p-3.5 border-t border-[#243246]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#ffb74d] text-[#0f1623] text-xs font-semibold flex items-center justify-center">
            {getInitials(user)}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-[#e8edf5] truncate">
              {[user?.nombre, user?.apellido].filter(Boolean).join(' ') || 'Veterinario'}
            </p>
            <p className="text-[10px] text-[#ffb74d] truncate">Vet. Dueño · {clinicName}</p>
          </div>
        </div>
      </div>
    </aside>
  )
}
